import * as React from 'react';
import { useTheme } from 'styled-components';
import ConfirmModal from '@sdk/components/ui/confirmModal/ConfirmModal';
import gettext from '@sdk/lang/gettext';
import StyledSvg from '@sdk/components/ui/StyledSvg';
import Column from '@sdk/components/ui/spacing/Column';
import Row from '@sdk/components/ui/spacing/Row';
import Button from '@sdk/components/ui/buttons/Button';
import RadioButton from '@sdk/components/forms/Radiobutton';
import HorizontalSeparator from '@sdk/components/ui/HorizontalSeparator';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { AutomatedBreakType } from '@sdk/settings/types';
import type { Shift } from '@sdk/automatedBreaks/types/Shift';

import TimeIconSvg from '@web/assets/svg/Icons/32/time-gray.svg';
import ClockedInBanner from '@web/timeClock/ClockedInBanner/ClockedInBanner';
import { useChooseBreakTypeModal } from '@web/timeClock/ChooseBreakTypeModal/chooseBreakTypeModal.hooks';
import { OnStartBreakProps } from '@web/timeClock/ChooseBreakTypeModal/chooseBreakTypeModal.types';
import { ButtonContainer } from '@web/timeClock/ChooseBreakTypeModal/chooseBreakTypeModal.styles';

const AUTOMATED_TYPE_FIELD_NAME = 'automatedType';

type Props = {
  onStartBreak: (props: OnStartBreakProps) => Promise<void>;
  shift: Shift;
  useWhiteBg?: boolean;
  useBtn150?: boolean;
};
const ChooseBreakTypeModal = ({
  onStartBreak,
  shift,
  useWhiteBg = false,
  useBtn150 = false,
}: Props) => {
  const theme = useTheme();
  const {
    isVisible,
    automatedType,
    setAutomatedType,
    canShowStartBreakButton,
    onClick,
    cancel,
    startBreak,
    canTakeMealBreak,
    canTakeRestBreak,
  } = useChooseBreakTypeModal({
    shift,
    onStartBreak,
  });

  if (!canShowStartBreakButton) {
    return null;
  }

  return (
    <>
      <ButtonContainer useWhiteBg={useWhiteBg} useBtn150={useBtn150}>
        <Button onClick={onClick} buttonType={ButtonType.warningInversed}>
          {gettext('Start break')}
        </Button>
      </ButtonContainer>

      {isVisible && (
        <ConfirmModal
          fullScreen
          bodyPadding={0}
          buttonsMargin={0}
          zIndex={2000}
          titleText={gettext('Start break')}
          titleIcon={<StyledSvg svg={TimeIconSvg} color={theme.colors[ThemeColorType.warning]} />}
          titleThemeColor={ThemeColorType.warning}
          primaryButtonText={gettext('Start break')}
          primaryButtonType={ButtonType.warning}
          secondaryButtonText={gettext('Cancel')}
          dismiss={cancel}
          onPrimaryClick={startBreak}
          primaryButtonDisabled={!automatedType}>
          <ClockedInBanner clockedIn={shift.clockedIn} />
          <Column
            pTop={20}
            pRight={20}
            pLeft={20}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (e.target.name === AUTOMATED_TYPE_FIELD_NAME) {
                setAutomatedType(e.target.value as any as AutomatedBreakType);
              }
            }}>
            {canTakeMealBreak && (
              <>
                <Row pBottom={20}>
                  <RadioButton
                    fieldName={AUTOMATED_TYPE_FIELD_NAME}
                    value={AutomatedBreakType.meal}
                    label={gettext('Meal break')}
                    checked={automatedType === AutomatedBreakType.meal}
                  />
                </Row>
                <HorizontalSeparator />
              </>
            )}
            {canTakeRestBreak && (
              <>
                <Row pTop={canTakeMealBreak ? 20 : 0} pBottom={20}>
                  <RadioButton
                    fieldName={AUTOMATED_TYPE_FIELD_NAME}
                    value={AutomatedBreakType.rest}
                    label={gettext('Rest break')}
                    checked={automatedType === AutomatedBreakType.rest}
                  />
                </Row>
                <HorizontalSeparator />
              </>
            )}
          </Column>
        </ConfirmModal>
      )}
    </>
  );
};

export default ChooseBreakTypeModal;
